import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Trophy, Flame, ThumbsUp, Play, Clock } from 'lucide-react';
import { useT } from '@/context/LocaleContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export default function WeeklyChallenge() {
  const { t } = useT();
  const { user } = useAuth();
  const [data, setData] = useState({ challenge: null, leaderboard: [] });

  const load = async () => {
    const { data } = await api.get('/challenges/current');
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const vote = async (postId) => {
    try {
      await api.post('/challenges/current/vote', { post_id: postId });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const ch = data.challenge;
  const endsAt = ch?.ends_at ? new Date(ch.ends_at) : null;
  const now = new Date();
  const hoursLeft = endsAt ? Math.max(0, Math.round((endsAt - now) / (1000 * 60 * 60))) : null;

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-5xl">
        <div className="mb-8 rise">
          <div className="overline text-orange-400 mb-2 flex items-center gap-2">
            <Trophy size={14} /> {t('challenge.title')}
          </div>
          {ch ? (
            <>
              <h1 className="font-display text-5xl tracking-tight">{ch.song_title}</h1>
              <div className="text-stone-400 mt-2">{ch.song_artist}</div>
              <p className="text-stone-300 mt-3 max-w-2xl">{ch.description}</p>
              {hoursLeft !== null && (
                <div className="mt-4 inline-flex items-center gap-2 text-sm text-stone-400">
                  <Clock size={14} /> {t('challenge.ends_in')} {Math.floor(hoursLeft / 24)}j {hoursLeft % 24}h
                </div>
              )}
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to={`/song/${ch.song_id}`} data-testid="challenge-open-song" className="btn-primary inline-flex items-center gap-2">
                  <Play size={16} /> Voir la chanson
                </Link>
                <Link to="/community" data-testid="challenge-publish" className="btn-ghost inline-flex items-center gap-2">
                  {t('challenge.your_entry')}
                </Link>
              </div>
            </>
          ) : (
            <div className="text-stone-400">Le défi de cette semaine arrive bientôt.</div>
          )}
        </div>

        <div className="mb-4">
          <h2 className="font-display text-3xl">{t('challenge.leaderboard')}</h2>
        </div>

        {data.leaderboard.length === 0 ? (
          <div className="text-stone-500 py-16 text-center font-display text-2xl">{t('challenge.no_entries')}</div>
        ) : (
          <div className="space-y-3">
            {data.leaderboard.map((row, i) => {
              const voted = ch?.votes?.[user?.user_id] === row.post_id;
              return (
                <div key={row.post_id} data-testid={`leaderboard-row-${i}`} className={`card-stone p-4 flex items-center gap-4 ${i === 0 ? 'border-orange-800/60' : ''}`}>
                  <div className={`font-display text-3xl w-10 shrink-0 ${i === 0 ? 'text-orange-400' : i === 1 ? 'text-stone-300' : i === 2 ? 'text-orange-700' : 'text-stone-500'}`}>#{row.rank}</div>
                  {row.user_picture ? (
                    <img src={row.user_picture} alt="" className="w-10 h-10 rounded-full shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-stone-700 flex items-center justify-center text-sm shrink-0">{row.user_name?.[0] || '?'}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{row.user_name}</div>
                    <div className="text-stone-400 text-sm truncate">{row.content}</div>
                    <div className="mt-1 text-xs text-stone-500 flex items-center gap-3">
                      <span className="inline-flex items-center gap-1"><Flame size={12} /> {row.reactions_total} réactions</span>
                      <span className="inline-flex items-center gap-1"><ThumbsUp size={12} /> {row.votes} votes</span>
                      <span className="text-orange-400 font-medium">Score {row.score}</span>
                    </div>
                  </div>
                  {row.user_id !== user?.user_id && (
                    <button
                      data-testid={`vote-btn-${i}`}
                      onClick={() => vote(row.post_id)}
                      className={`btn-ghost text-xs inline-flex items-center gap-1 ${voted ? 'border-orange-600 text-orange-400' : ''}`}
                    >
                      <ThumbsUp size={12} /> {voted ? t('challenge.voted') : t('challenge.vote')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
